/**
 * Unified execution history types for both Data Collection and VIN Processing
 */

import { ExecutionHistory as DataCollectionExecutionHistory } from '@/services/api/dataCollection';
import { ExecutionHistory as VINProcessingExecutionHistory } from '@/services/api/vinProcessor';

/**
 * Execution type to distinguish between different services
 */
export type ExecutionType = 'data_collection' | 'vin_processing';

/**
 * Unified execution history that can represent both data collection and VIN processing executions
 */
export interface UnifiedExecutionHistory {
  // Common fields
  id: string; // Unique identifier (run_id for data collection, job_id for VIN processing)
  executionType: ExecutionType;
  status: string;
  started_at: string | null;
  finished_at?: string | null;
  duration_seconds?: number | null;

  // Data Collection specific fields
  run_id?: string; // Apify run ID (data collection)
  task_name?: string;
  workflow_name?: string;
  oem?: string | null;
  zone?: string | null;
  records_processed?: number | null;
  inventory_summary?: unknown;
  validation_status?: string | null;
  gcs_path?: string | null;

  // VIN Processing specific fields
  job_id?: string; // Job ID (VIN processing)
  entry_point?: string;
  input_params?: Record<string, unknown>;
  total_vins?: number;
  processed_vins?: number;
  failed_vins?: number;
  tenant_id?: string;
  requested_by?: string | null;
  requested_at?: string;
  // Enhanced Step 2 metrics (OpenAPI 2.0.0)
  processed_vins_list?: string[] | null; // Array of successfully processed VINs (for <= 1000 VINs)
  processed_vins_list_gcs?: string | null; // GCS path to processed VINs list file (for > 1000 VINs)
  failed_vins_list?: string[] | null; // Array of failed VINs (for <= 1000 VINs)
  failed_vins_list_gcs?: string | null; // GCS path to failed VINs list file (for > 1000 VINs)
  total_recalls?: number | null; // Total count of all recalls (from vin_recall_details table)
  recalls_with_advance_notice?: number | null; // Count of recalls with advance_notice = 'Y'

  // Common metadata
  errors?: unknown;
  output_summary?: Record<string, unknown> | null;
}

/**
 * Calculate duration from timestamps when duration_seconds is not available
 */
function calculateDurationFromTimestamps(
  startedAt: string | null | undefined,
  finishedAt: string | null | undefined
): number | null {
  if (!startedAt || !finishedAt) return null;
  const start = new Date(startedAt).getTime();
  const end = new Date(finishedAt).getTime();
  if (isNaN(start) || isNaN(end)) return null;
  return Math.round((end - start) / 1000);
}

/**
 * Convert Data Collection execution to unified format
 */
export function normalizeDataCollectionExecution(
  exec: DataCollectionExecutionHistory
): UnifiedExecutionHistory {
  const finishedAt = exec.finished_at || exec.completed_at || undefined;
  // Calculate duration from timestamps if duration_seconds is not provided
  const durationSeconds =
    exec.duration_seconds ?? calculateDurationFromTimestamps(exec.started_at, finishedAt);

  return {
    id: exec.run_id || exec.id || `dc-${Date.now()}`,
    executionType: 'data_collection',
    status: exec.status,
    started_at: exec.started_at,
    finished_at: finishedAt,
    duration_seconds: durationSeconds,
    run_id: exec.run_id,
    task_name: exec.task_name,
    workflow_name: exec.workflow_name,
    oem: exec.oem,
    zone: exec.zone,
    records_processed: exec.records_processed || exec.records || null,
    inventory_summary: exec.inventory_summary,
    validation_status: exec.validation_status,
    gcs_path: exec.gcs_path,
    errors: exec.errors
  };
}

/**
 * Convert VIN Processing execution to unified format
 * Detects failed jobs even if status is marked as "running"
 */
// eslint-disable-next-line max-lines-per-function, complexity
export function normalizeVINProcessingExecution(
  exec: VINProcessingExecutionHistory
): UnifiedExecutionHistory {
  // Detect if job should be marked as failed even if status is "running"
  let status = exec.status;
  const statusLower = exec.status?.toLowerCase() || '';

  // If status is "running", check if it should actually be "failed"
  if (statusLower === 'running' || statusLower === 'pending') {
    // Check if job has errors array with actual errors
    const hasErrors = exec.errors && Array.isArray(exec.errors) && exec.errors.length > 0;

    // Check if all VINs failed (failed_vins equals total_vins and processed_vins is 0)
    const allVinsFailed =
      exec.total_vins > 0 && exec.failed_vins === exec.total_vins && exec.processed_vins === 0;

    // Check if some VINs failed but others processed
    const hasPartialFailures =
      exec.failed_vins > 0 && exec.processed_vins > 0 && exec.failed_vins < exec.total_vins;

    // Check if job has finished_at timestamp but status is still "running" (stale status)
    // This indicates the job completed but status wasn't updated in database
    const hasFinishedTimestamp = exec.finished_at && exec.finished_at !== null;

    // Check if job has been running for too long (> 24 hours) with no progress
    // Only mark as failed if there's been absolutely no progress (both processed and failed are 0)
    const isStale = exec.started_at && !exec.finished_at;
    if (isStale) {
      const startTime = new Date(exec.started_at).getTime();
      const now = Date.now();
      const hoursRunning = (now - startTime) / (1000 * 60 * 60);
      // If running > 24 hours with no progress at all, likely failed
      const isStaleRunning =
        hoursRunning > 24 &&
        exec.processed_vins === 0 &&
        exec.failed_vins === 0 &&
        exec.total_vins > 0;

      if (isStaleRunning || hasErrors || allVinsFailed || hasFinishedTimestamp) {
        if (hasPartialFailures || (hasErrors && exec.processed_vins > 0)) {
          status = 'partial_success';
        } else {
          status = 'failed';
        }
      }
    } else if (hasErrors || allVinsFailed || hasFinishedTimestamp) {
      // Job has finished but status wasn't updated - mark as partial success or failed
      if (hasPartialFailures || (hasErrors && exec.processed_vins > 0)) {
        status = 'partial_success';
      } else {
        status = 'failed';
      }
    }
  }

  // Calculate duration from timestamps if duration_seconds is not provided
  const durationSeconds =
    exec.duration_seconds ?? calculateDurationFromTimestamps(exec.started_at, exec.finished_at);

  // Extract OEM and zone from input_params for easier access
  // Check multiple possible field names as backend may vary
  const inputParams = exec.input_params || {};
  const oem = (inputParams.make_name ||
    inputParams.makeName ||
    inputParams.oem ||
    inputParams.OEM ||
    inputParams.make ||
    inputParams.Make) as string | null | undefined;
  const zone = (inputParams.zone_slug ||
    inputParams.zoneSlug ||
    inputParams.zone ||
    inputParams.Zone) as string | null | undefined;

  return {
    id: exec.job_id || exec.id || `vin-${Date.now()}`,
    executionType: 'vin_processing',
    status,
    started_at: exec.started_at || null,
    finished_at: exec.finished_at || null,
    duration_seconds: durationSeconds,
    job_id: exec.job_id,
    entry_point: exec.entry_point,
    input_params: exec.input_params,
    // Extract OEM and zone for easier access in UI
    oem: oem || null,
    zone: zone || null,
    total_vins: exec.total_vins,
    processed_vins: exec.processed_vins,
    failed_vins: exec.failed_vins,
    tenant_id: exec.tenant_id,
    requested_by: exec.requested_by,
    requested_at: exec.requested_at,
    errors: exec.errors,
    output_summary: exec.output_summary,
    // Enhanced Step 2 metrics (OpenAPI 2.0.0)
    processed_vins_list: exec.processed_vins_list,
    processed_vins_list_gcs: exec.processed_vins_list_gcs,
    failed_vins_list: exec.failed_vins_list,
    failed_vins_list_gcs: exec.failed_vins_list_gcs,
    total_recalls: exec.total_recalls,
    recalls_with_advance_notice: exec.recalls_with_advance_notice
  };
}

/**
 * Get display name for execution based on type
 */
export function getExecutionDisplayName(exec: UnifiedExecutionHistory): string {
  if (exec.executionType === 'data_collection') {
    return exec.workflow_name || exec.task_name || 'Data Collection';
  } else {
    // VIN Processing
    if (exec.entry_point === 'inventory_date_make') {
      return 'Process VINs from Inventory';
    } else if (exec.entry_point === 'step1_execution') {
      return 'Process VINs from Data Collection';
    } else if (exec.entry_point === 'apify_run_id') {
      return 'Process VINs from Apify Run';
    }
    return 'VIN Processing';
  }
}

/**
 * Get record count for display
 */
export function getExecutionRecordCount(exec: UnifiedExecutionHistory): number | null {
  if (exec.executionType === 'data_collection') {
    return exec.records_processed || null;
  } else {
    return exec.total_vins || null;
  }
}
